"use client";
import { K as useRender } from "../chunks/vendor-base-ui-n0ony04mhklkmnfd.js";
import { n as useObservabilitySafe, t as useImpressionRef } from "../chunks/use-impression-ref-k081rlv0dt8gci3d.js";
import { r as useAccessibleNameWarning } from "../chunks/accessible-name-h2kn8kxv0zjyg24j.js";
import { t as cn } from "../chunks/cn-dk9nmc4mhy1765ud.js";
import { n as useLinkRenderer } from "../chunks/link-provider-erp27xeaxlv7jgvb.js";
import React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/button/button.tsx
/** Button variant definitions mapping size and variant names to their Tailwind classes. */
var ATOMS_BUTTON_VARIANTS = {
	size: {
		sm: {
			classes: "h-8 gap-1 rounded-xl px-4 text-label-md",
			description: "Small button for secondary actions"
		},
		md: {
			classes: "h-11 gap-1 rounded-2xl px-5 text-label-md",
			description: "Medium button for compact contexts"
		},
		lg: {
			classes: "h-14 gap-1.5 rounded-2xl px-6 text-label-lg",
			description: "Large button (default)"
		}
	},
	variant: {
		primary: {
			classes: "border border-od-border-accent-brand bg-od-bg-accent-brand !text-od-content-inverse-primary not-disabled:hover:border-od-border-hover-accent-brand not-disabled:hover:bg-od-bg-hover-accent-brand not-disabled:active:border-od-border-pressed-accent-brand not-disabled:active:bg-od-bg-pressed-accent-brand disabled:not-aria-busy:border-transparent disabled:not-aria-busy:bg-od-bg-inverse-inactive-primary disabled:not-aria-busy:!text-od-content-tertiary focus-visible:outline-od-border-focused-accent-brand",
			description: "High-emphasis button for primary actions"
		},
		secondary: {
			classes: "border border-od-border-primary bg-od-bg-primary !text-od-content-primary not-disabled:hover:border-od-border-hover-secondary not-disabled:hover:bg-od-bg-hover-primary not-disabled:active:border-od-border-hover-primary not-disabled:active:bg-od-bg-hover-primary disabled:not-aria-busy:border-transparent disabled:not-aria-busy:bg-od-bg-inverse-inactive-primary disabled:not-aria-busy:!text-od-content-tertiary focus-visible:outline-od-border-focused-secondary data-[state=open]:bg-od-bg-primary",
			description: "Default button style for most actions"
		},
		secondaryInverse: {
			classes: "border border-transparent bg-od-bg-inverse-secondary !text-od-content-inverse-primary not-disabled:hover:border-od-border-inverse-hover-secondary not-disabled:hover:bg-od-bg-inverse-hover-secondary not-disabled:active:border-od-border-inverse-pressed-secondary not-disabled:active:bg-od-bg-inverse-pressed-secondary disabled:not-aria-busy:border-transparent disabled:not-aria-busy:bg-od-bg-inverse-inactive-primary disabled:not-aria-busy:!text-od-content-tertiary focus-visible:outline-od-border-focused-secondary",
			description: "Secondary button for use on inverted/dark surfaces"
		},
		destructive: {
			classes: "border border-od-border-error bg-od-bg-error !text-od-content-inverse-primary not-disabled:hover:border-od-border-hover-accent-error not-disabled:hover:bg-od-bg-hover-accent-error not-disabled:active:border-od-border-pressed-accent-error not-disabled:active:bg-od-bg-pressed-accent-error disabled:not-aria-busy:border-transparent disabled:not-aria-busy:bg-od-bg-inverse-inactive-primary disabled:not-aria-busy:!text-od-content-tertiary focus-visible:outline-od-border-focused-accent-error",
			description: "High-emphasis red button for destructive actions"
		}
	}
};
var ATOMS_BUTTON_DEFAULT_VARIANTS = {
	size: "lg",
	variant: "primary"
};
function buttonVariants({ variant = ATOMS_BUTTON_DEFAULT_VARIANTS.variant, size = ATOMS_BUTTON_DEFAULT_VARIANTS.size } = {}) {
	return cn("group flex w-max shrink-0 items-center justify-center select-none", "transition-transform not-disabled:active:scale-95", "focus:outline-none focus-visible:outline-solid focus-visible:outline-1 focus-visible:outline-offset-2", "cursor-pointer", "disabled:cursor-not-allowed", ATOMS_BUTTON_VARIANTS.variant[variant].classes, ATOMS_BUTTON_VARIANTS.size[size].classes);
}
var BUTTON_LOADER_DOT_CLASS = {
	sm: "size-1.25",
	md: "size-1.5",
	lg: "size-1.75"
};
var BUTTON_LOADER_GAP_CLASS = {
	sm: "gap-1",
	md: "gap-1",
	lg: "gap-1"
};
var BUTTON_LOADER_MIN_CLASS = {
	primary: "[--atoms-button-loader-min:0.4]",
	secondary: "[--atoms-button-loader-min:0.3]",
	secondaryInverse: "[--atoms-button-loader-min:0.4]",
	destructive: "[--atoms-button-loader-min:0.4]"
};
var BUTTON_LOADER_DOT_ANIMATIONS = [
	"animate-[atoms-button-loader-1_1900ms_ease-in-out_infinite]",
	"animate-[atoms-button-loader-2_1900ms_ease-in-out_infinite]",
	"animate-[atoms-button-loader-3_1900ms_ease-in-out_infinite]",
	"animate-[atoms-button-loader-4_1900ms_ease-in-out_infinite]"
];
function ButtonLoadingDots({ size, variant }) {
	return /* @__PURE__ */ jsx("span", {
		role: "status",
		"aria-label": "Loading",
		className: cn("absolute inset-0 flex items-center justify-center", BUTTON_LOADER_GAP_CLASS[size], BUTTON_LOADER_MIN_CLASS[variant]),
		children: BUTTON_LOADER_DOT_ANIMATIONS.map((animClass) => /* @__PURE__ */ jsx("span", { className: cn("rounded-full bg-current", BUTTON_LOADER_DOT_CLASS[size], animClass) }, animClass))
	});
}
/**
* Button form — renders a native `<button>`. Selected by {@link Button} when no
* `href` is given. Owns the `loading` affordance (the only form that supports it).
*/
var ButtonAsButton = React.forwardRef(function ButtonAsButton(props, ref) {
	const { children, className, disabled, loading, type, size = ATOMS_BUTTON_DEFAULT_VARIANTS.size, variant = ATOMS_BUTTON_DEFAULT_VARIANTS.variant, analyticsName, trackImpression, onClick, ...rest } = props;
	const { trackJourneyEvent } = useObservabilitySafe();
	const viewRef = useImpressionRef(Boolean(analyticsName && trackImpression), analyticsName, "buttonViewEvent", ref);
	useAccessibleNameWarning("Button", Boolean(props["aria-label"]));
	const handleClick = React.useCallback((event) => {
		if (analyticsName) trackJourneyEvent("platform", "buttonClickEvent", { targetName: analyticsName });
		onClick?.(event);
	}, [
		analyticsName,
		onClick,
		trackJourneyEvent
	]);
	return /* @__PURE__ */ jsxs("button", {
		ref: viewRef,
		type: type ?? "button",
		disabled: disabled || loading,
		"aria-busy": loading || void 0,
		className: cn(buttonVariants({
			variant,
			size
		}), "relative", className),
		onClick: handleClick,
		...rest,
		children: [loading && /* @__PURE__ */ jsx(ButtonLoadingDots, {
			size,
			variant
		}), /* @__PURE__ */ jsx("span", {
			"aria-hidden": loading || void 0,
			className: cn("contents", loading && "invisible"),
			children
		})]
	});
});
ButtonAsButton.displayName = "Button";
/**
* Link form — renders an anchor styled as a button, routed through
* `LinkProvider`. Selected by {@link Button} when `href` is given.
*/
var ButtonAsLink = React.forwardRef(function ButtonAsLink(props, ref) {
	const { children, className, external, href, disabled, size = ATOMS_BUTTON_DEFAULT_VARIANTS.size, variant = ATOMS_BUTTON_DEFAULT_VARIANTS.variant, analyticsName, trackImpression, onClick, ...rest } = props;
	const renderLink = useLinkRenderer();
	const { trackJourneyEvent } = useObservabilitySafe();
	useAccessibleNameWarning("Button", Boolean(props["aria-label"]));
	const viewRef = useImpressionRef(Boolean(analyticsName && trackImpression), analyticsName, "linkViewEvent", ref);
	const handleClick = React.useCallback((event) => {
		if (analyticsName) trackJourneyEvent("platform", "linkClickEvent", { targetName: analyticsName });
		onClick?.(event);
	}, [
		analyticsName,
		onClick,
		trackJourneyEvent
	]);
	const linkDisabled = Boolean(disabled);
	return useRender({
		render: renderLink,
		defaultTagName: "a",
		ref: viewRef,
		props: {
			className: cn(buttonVariants({
				variant,
				size
			}), "flex items-center no-underline!", linkDisabled && "pointer-events-none", className),
			href: linkDisabled ? void 0 : href,
			to: linkDisabled || typeof href !== "string" ? void 0 : href,
			"aria-disabled": linkDisabled || void 0,
			onClick: handleClick,
			...rest,
			...external ? {
				target: "_blank",
				rel: [
					rest.rel,
					"noopener",
					"noreferrer"
				].filter(Boolean).join(" ")
			} : {},
			children
		}
	});
});
ButtonAsLink.displayName = "Button";
/**
* Primary action trigger. Renders a native `<button>` by default; pass `href` to
* render an anchor styled identically (routed through `LinkProvider`). Supports
* multiple variants and sizes, plus a `loading` state on the button form.
* Compose icons by placing them inside `children`.
*
* @example
* ```tsx
* <Button aria-label="Save">Save</Button>
* <Button aria-label="New listing" variant="secondary"><IconPlus />New listing</Button>
* <Button aria-label="Settings" href="/settings" variant="secondary">Settings</Button>
* <Button aria-label="Visit Site" href="https://example.com" external>Visit Site</Button>
* ```
*/
var Button = React.forwardRef(function Button(props, ref) {
	if (props.href != null) return /* @__PURE__ */ jsx(ButtonAsLink, {
		ref,
		...props
	});
	return /* @__PURE__ */ jsx(ButtonAsButton, {
		ref,
		...props
	});
});
Button.displayName = "Button";
//#endregion
export { ATOMS_BUTTON_DEFAULT_VARIANTS, ATOMS_BUTTON_VARIANTS, Button, buttonVariants };

